/**
 * <park-hero> — home page hero band (epic #138, issue #143; see
 * docs/design-system-rebuild-plan.md).
 *
 * Single source of truth = the pure `renderParkHero()` function below, consumed
 * by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkHero.astro`,
 *     which calls it server-side and emits the markup inside <park-hero data-ssr>
 *     so the hero is in the initial HTML (SEO / no-JS), and
 *   - the Claude-Design preview, where the custom element renders it client-side.
 * Because both call the SAME function, server and client markup are identical.
 *
 * Emits the live `.home-hero*` class names verbatim; the layered `::before` /
 * `::after` forest background, 2-col grid and 860px reorder live in
 * `wc/css/park-hero.css` (lifted 1:1 from HomePage.astro's scoped <style>).
 *
 * Attributes:
 *   - badge="…"     pill text (the 🐾 prefix is added here).
 *   - heading="…"   the <h1>.
 *   - lead="…"      the lead paragraph.
 *   - trust="a|b|c" pipe-delimited trust-list items (each prefixed with ✓).
 * Slotted content (named slots, kept server-rendered for locale hrefs / EmDash
 * <Image> / i18n): "cta" (the two park-btn anchors) and "media" (the <img>).
 *
 * @dsSpec design-system#FR-3 — the landing hero band of the home page.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-hero";

export interface ParkHeroProps {
	badge?: string;
	heading?: string;
	lead?: string;
	trust?: string[];
}

/**
 * Pure render: (props, CTA HTML, media HTML) → inner HTML string. No DOM access,
 * so it is safe to call server-side (the .astro wrapper) and client-side (the
 * element). `ctaHtml` / `mediaHtml` are trusted author markup (already rendered
 * by the caller — they carry locale hrefs and specific img src/dims).
 */
export function renderParkHero(
	props: ParkHeroProps,
	ctaHtml: string,
	mediaHtml: string,
): string {
	const badge = props.badge
		? html`<span class="home-hero__badge">🐾 ${esc(props.badge)}</span>`
		: "";
	const heading = props.heading ? html`<h1>${esc(props.heading)}</h1>` : "";
	const lead = props.lead
		? html`<p class="home-hero__lead">${esc(props.lead)}</p>`
		: "";
	const trust = (props.trust ?? []).length
		? html`<ul class="home-hero__trust">${(props.trust ?? [])
				.map((item) => html`<li>✓ ${esc(item)}</li>`)
				.join("")}</ul>`
		: "";
	return html`<section class="home-hero">
		<div class="park-wrap home-hero__inner">
			<div class="home-hero__text">
				${badge}
				${heading}
				${lead}
				<div class="home-hero__cta">${ctaHtml}</div>
				${trust}
			</div>
			<div class="home-hero__media">${mediaHtml}</div>
		</div>
	</section>`;
}

class ParkHero extends ParkElement {
	static get observedAttributes(): string[] {
		return ["badge", "heading", "lead", "trust"];
	}

	protected render(): string {
		const trust = this.attr("trust")
			? this.attr("trust")
					.split("|")
					.map((s) => s.trim())
					.filter(Boolean)
			: [];
		// Client-side path: slots arrive as the captured light-DOM inner HTML.
		// CTAs and media are author-slotted; lacking a slot-routing runtime here,
		// the whole captured slot is treated as the CTA region (the SSR wrapper is
		// the authoritative live path and routes the named slots explicitly).
		return renderParkHero(
			{
				badge: this.attr("badge"),
				heading: this.attr("heading"),
				lead: this.attr("lead"),
				trust,
			},
			this.slotHtml,
			"",
		);
	}
}

defineElement(TAG, ParkHero);

export { ParkHero };
